import {
  parseScore,
  ScoreParseError,
  summarizeScore,
  type ParsedScore,
  type PartAssignment,
  type PartSummary,
  type ScoreSummary,
  type SummaryWarning,
} from '@corocanto/scoring';
import { type ChangeEvent, type DragEvent, type ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { ApiError } from '../api/client';
import { useUploadSong } from '../api/songs';
import { useMembership } from '../auth/session';
import { formatBpm, formatDuration, formatPlaybackOrder, formatRange, pitchName } from '../lib/format';
import { barList } from '../lib/lines';
import ui from '../styles/ui.module.css';
import styles from './SongUploadPage.module.css';

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = '.musicxml,.xml,.mxl';
const ASSIGNMENTS: PartAssignment[] = ['S', 'A', 'T', 'B', 'accompaniment'];

interface Loaded {
  file: File;
  score: ParsedScore;
}

/**
 * Director uploads a song: pick a file, see what the scoring package found (parts and their
 * S/A/T/B match, accompaniment, bars, lyrics, repeats, tempo, treble-8 tenor), fix the part
 * assignment if needed, then confirm to store the file and the summary.
 */
export function SongUploadPage() {
  const { t } = useTranslation();
  const membership = useMembership();
  const navigate = useNavigate();
  const upload = useUploadSong(membership?.choirId ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [assignments, setAssignments] = useState<Record<string, PartAssignment>>({});
  const [title, setTitle] = useState('');
  const [composer, setComposer] = useState('');

  const summary = useMemo(
    () => (loaded ? summarizeScore(loaded.score, { assignments }) : null),
    [loaded, assignments],
  );

  async function readFile(file: File) {
    setLoaded(null);
    setFileError(null);
    setAssignments({});
    upload.reset();
    if (!/\.(musicxml|xml|mxl)$/i.test(file.name)) {
      setFileError(t('apiErrors.invalidFileType'));
      return;
    }
    if (file.size > MAX_BYTES) {
      setFileError(t('apiErrors.fileTooLarge'));
      return;
    }
    setReading(true);
    try {
      const score = parseScore(new Uint8Array(await file.arrayBuffer()));
      const initial = summarizeScore(score);
      setLoaded({ file, score });
      setTitle(initial.title ?? file.name.replace(/\.[^.]+$/, ''));
      setComposer(initial.composer ?? initial.arranger ?? '');
    } catch (e) {
      if (e instanceof ScoreParseError) {
        setFileError(
          e.code === 'malformedXml' && e.line !== null
            ? t('scoreErrors.malformedXmlLine', { line: e.line })
            : t(`scoreErrors.${e.code}`),
        );
      } else {
        setFileError(t('scoreErrors.unknown'));
      }
    } finally {
      setReading(false);
    }
  }

  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) void readFile(file);
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) void readFile(file);
  }

  function save() {
    if (!loaded || !summary || !title.trim()) return;
    upload.mutate(
      { file: loaded.file, summary, title, composer },
      { onSuccess: (song) => navigate('/library', { state: { uploadedTitle: song.title } }) },
    );
  }

  const uploadError =
    upload.error instanceof ApiError
      ? t(`apiErrors.${upload.error.code ?? 'unknown'}`, { defaultValue: t('apiErrors.unknown') })
      : upload.error
        ? t('apiErrors.unknown')
        : null;

  const fileInput = (
    <input ref={inputRef} type="file" accept={ACCEPT} className={styles.hiddenInput} onChange={onInputChange} data-testid="score-file" />
  );

  return (
    <div className={ui.page}>
      <div className={ui.pageHeader}>
        <div>
          <Link to="/library" className={styles.back}>
            {t('upload.back')}
          </Link>
          <h1>{t('upload.title')}</h1>
          <p className={`${ui.subtitle} ${styles.intro}`}>{t('upload.intro')}</p>
        </div>
      </div>

      {fileError && (
        <div className={`${ui.notice} ${ui.noticeError}`} role="alert" data-testid="file-error">
          {fileError}
        </div>
      )}

      {!loaded && (
        <section
          className={`${ui.card} ${styles.dropzone} ${dragging ? styles.dragging : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <p className={styles.dropHint}>{reading ? t('upload.reading') : t('upload.dropHint')}</p>
          <p className={ui.muted}>{t('upload.accept')}</p>
          <button type="button" className={`${ui.button} ${ui.primary}`} onClick={() => inputRef.current?.click()} disabled={reading}>
            {t('upload.choose')}
          </button>
          {fileInput}
        </section>
      )}

      {loaded && summary && (
        <>
          <section className={ui.card}>
            <div className={styles.foundHeader}>
              <h2 className={ui.cardTitle}>{t('upload.found')}</h2>
              <div className={styles.fileName}>
                <span className={ui.muted}>{t('upload.fileLabel')}:</span> {loaded.file.name}
                <button type="button" className={ui.linkButton} onClick={() => inputRef.current?.click()}>
                  {t('upload.another')}
                </button>
                {fileInput}
              </div>
            </div>
            <div className={styles.titleFields}>
              <label className={ui.field}>
                <span className={ui.label}>{t('upload.titleLabel')}</span>
                <input className={ui.input} value={title} onChange={(e) => setTitle(e.target.value)} name="title" maxLength={200} />
              </label>
              <label className={ui.field}>
                <span className={ui.label}>{t('upload.composerLabel')}</span>
                <input className={ui.input} value={composer} onChange={(e) => setComposer(e.target.value)} name="composer" maxLength={200} />
              </label>
            </div>
            <Facts summary={summary} />
          </section>

          <section className={ui.card}>
            <h2 className={ui.cardTitle}>{t('upload.parts')}</h2>
            <p className={`${ui.muted} ${styles.hint}`}>{t('upload.partsHint')}</p>
            <PartsTable
              summary={summary}
              onAssign={(partId, value) => setAssignments((prev) => ({ ...prev, [partId]: value }))}
            />
          </section>

          <section className={ui.card}>
            <h2 className={ui.cardTitle}>{t('upload.warnings')}</h2>
            <Warnings summary={summary} />
          </section>

          <div className={styles.actions}>
            {uploadError && (
              <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
                {uploadError}
              </div>
            )}
            {!title.trim() && <span className={ui.muted}>{t('upload.titleMissing')}</span>}
            <button
              type="button"
              className={`${ui.button} ${ui.primary}`}
              onClick={save}
              disabled={upload.isPending || !title.trim()}
            >
              {upload.isPending ? t('upload.saving') : t('upload.confirm')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Facts({ summary }: { summary: ScoreSummary }) {
  const { t } = useTranslation();
  const tenor = summary.parts.find((p) => p.voicePart === 'T');
  const accompaniment = summary.parts.filter((p) => p.role === 'accompaniment');
  const later = summary.tempoChanges.slice(1);

  const rows: [string, ReactNode][] = [
    [
      t('upload.bars'),
      <>
        {t('upload.barsValue', { count: summary.measureCount })} (
        {t('upload.barsRange', { first: summary.firstMeasureNumber, last: summary.lastMeasureNumber })})
        {summary.hasPickup && <>, {t('upload.pickup')}</>}
        {summary.playbackMeasureCount !== summary.measureCount && (
          <span className={ui.secondary}>{t('upload.playbackBars', { count: summary.playbackMeasureCount })}</span>
        )}
      </>,
    ],
    [t('upload.timeSignature'), summary.timeSignatures.map((ts) => `${ts.beats}/${ts.beatType}`).join(', ')],
    [
      t('upload.tempo'),
      summary.tempo ? (
        <>
          ♩ = {formatBpm(summary.tempo.quarterBpm)}
          {later.length > 0 && (
            <span className={ui.secondary}>
              {later.map((c) => t('upload.tempoChange', { bar: c.measureNumber, bpm: formatBpm(c.quarterBpm) })).join(' · ')}
            </span>
          )}
        </>
      ) : (
        t('upload.tempoNone')
      ),
    ],
    [t('upload.duration'), t('upload.durationValue', { time: formatDuration(summary.durationSeconds) })],
    [
      t('upload.repeats'),
      summary.repeats.length === 0
        ? t('upload.noRepeats')
        : summary.repeats.map((r, i) => (
            <span key={i} className={styles.repeatLine}>
              {t('upload.repeatSection', { range: formatRange(r.startMeasureNumber, r.endMeasureNumber), times: r.times })}
              {r.endings.map((e) => (
                <span key={e.numbers.join(',')}>
                  {' · '}
                  {t('upload.ending', {
                    numbers: e.numbers.join(', '),
                    range: formatRange(e.firstMeasureNumber, e.lastMeasureNumber),
                  })}
                </span>
              ))}
            </span>
          )),
    ],
    [t('upload.playbackOrder'), formatPlaybackOrder(summary.playbackOrder, summary.measureNumbers)],
  ];
  if (summary.jumps.length > 0) {
    rows.push([t('upload.jumps'), summary.jumps.map((j) => `${t(`jumps.${j.type}`)} (${j.measureNumber})${j.withRepeats ? ` ${t('upload.withRepeats')}` : ''}`).join(', ')]);
  }
  if (summary.rehearsalMarks.length > 0) {
    rows.push([
      t('upload.rehearsalMarks'),
      summary.rehearsalMarks.map((r) => `${r.label} (${r.measureNumber})`).join(', '),
    ]);
  }
  rows.push([
    t('upload.tenorClef'),
    !tenor ? t('upload.noTenor') : summary.tenorTreble8 ? <strong>{t('upload.tenor8')}</strong> : t('upload.tenorNot8'),
  ]);
  // Parts that divide into two lines (divisi): chorists choose theirs while practising.
  const divided = summary.dividedParts ?? [];
  rows.push([
    t('upload.lines'),
    divided.length === 0 ? (
      t('upload.noLines')
    ) : (
      <span data-testid="divided-parts">
        {divided
          .map((d) =>
            t(d.source === 'staves' ? 'upload.linesStaves' : 'upload.linesStaff', {
              part: t(`parts.${d.voicePart}`),
              bars: barList(d.measureNumbers, summary.measureNumbers),
            }),
          )
          .join('; ')}
      </span>
    ),
  ]);
  rows.push([
    t('upload.accompaniment'),
    accompaniment.length === 0
      ? t('upload.noAccompaniment')
      : t('upload.accompanimentValue', {
          names: accompaniment.map((p) => `${p.name || p.id} (${t('upload.staves', { count: p.staffCount })})`).join(', '),
        }),
  ]);

  return (
    <dl className={styles.facts} data-testid="facts">
      {rows.map(([label, value]) => (
        <div key={label} className={styles.fact}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function PartsTable({
  summary,
  onAssign,
}: {
  summary: ScoreSummary;
  onAssign: (partId: string, value: PartAssignment) => void;
}) {
  const { t } = useTranslation();
  const noteNames = t('notes.names', { returnObjects: true }) as string[];
  const current = (p: PartSummary): string => (p.role === 'accompaniment' ? 'accompaniment' : (p.voicePart ?? ''));

  return (
    <table className={ui.table} data-testid="parts-table">
      <thead>
        <tr>
          <th>{t('upload.colStaff')}</th>
          <th>{t('upload.colAssign')}</th>
          <th>{t('upload.colClef')}</th>
          <th>{t('upload.colRange')}</th>
          <th>{t('upload.colNotes')}</th>
          <th>{t('upload.colLyrics')}</th>
        </tr>
      </thead>
      <tbody>
        {summary.parts.map((p) => (
          <tr key={p.id} data-testid={`part-${p.id}`}>
            <td>
              {p.name || p.id}
              {p.staffCount > 1 && <span className={ui.secondary}>{t('upload.staves', { count: p.staffCount })}</span>}
            </td>
            <td>
              <select
                className={ui.select}
                value={current(p)}
                aria-label={t('upload.colAssign')}
                onChange={(e) => onAssign(p.id, e.target.value as PartAssignment)}
              >
                {current(p) === '' && <option value="">{t('upload.choose_part')}</option>}
                {ASSIGNMENTS.map((a) => (
                  <option key={a} value={a}>
                    {t(`parts.${a}`)}
                  </option>
                ))}
              </select>
              <span className={ui.secondary}>{t(`upload.matchedBy.${p.matchedBy ?? (p.role === 'accompaniment' ? 'auto' : 'none')}`)}</span>
            </td>
            <td>{p.clefs.map((c) => t(`clefs.${c.kind}`)).join(', ')}</td>
            <td>{p.range ? `${pitchName(p.range.lowestMidi, noteNames)}–${pitchName(p.range.highestMidi, noteNames)}` : '–'}</td>
            <td>{p.noteCount}</td>
            <td className={styles.lyricsCell}>
              {p.lyrics.syllableCount === 0 ? (
                <span className={ui.muted}>{t('upload.noLyricsShort')}</span>
              ) : (
                <>
                  {t('upload.lyricsSummary', { syllables: p.lyrics.syllableCount, verses: p.lyrics.verses.length })}
                  <span className={ui.secondary}>“{p.lyrics.preview}”</span>
                </>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Warnings({ summary }: { summary: ScoreSummary }) {
  const { t } = useTranslation();
  if (summary.warnings.length === 0) {
    return (
      <div className={`${ui.notice} ${ui.noticeSuccess}`} data-testid="no-warnings">
        {t('upload.noWarnings')}
      </div>
    );
  }
  const text = (w: SummaryWarning) => {
    const part = summary.parts.find((p) => p.id === w.partId);
    return t(`warnings.${w.code}`, {
      name: part ? part.name || part.id : '',
      part: w.voicePart ? t(`parts.${w.voicePart}`) : '',
    });
  };
  return (
    <ul className={styles.warnings} data-testid="warnings">
      {summary.warnings.map((w, i) => (
        <li key={i} className={`${ui.notice} ${ui.noticeWarning}`}>
          {text(w)}
        </li>
      ))}
    </ul>
  );
}
